import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabaseClient";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let unsub = null;

    async function init() {
      setLoading(true);

      // 1) Bootstrap from session (works best across browsers)
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (!mounted) return;

      if (sessionErr) {
        // If anything goes wrong, fail gracefully (no crash)
        setSession(null);
        setLoading(false);
      } else {
        setSession(sessionData?.session ?? null);
        setLoading(false);
      }

      // 2) Keep in sync
      const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
        if (!mounted) return;
        setSession(newSession ?? null);
        setLoading(false);
      });

      unsub = sub?.subscription;
    }

    init();

    return () => {
      mounted = false;
      unsub?.unsubscribe?.();
    };
  }, []);

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      signOut: () => supabase.auth.signOut(),
    }),
    [session, loading]
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  return ctx;
}